/**
 * Position Sizing page - "How much should I buy given my risk tolerance?"
 *
 * Accepts ?symbol= query param to pre-fill the symbol field.
 * Example: /what-if/position?symbol=NVDA
 */
import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePositionSizing, usePrice } from '../hooks'
import { Input } from '../components/common'
import type { PositionSizingResult } from '../types'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

export function PositionSizingPage() {
  const { calculate, holdings } = usePositionSizing()
  const [searchParams] = useSearchParams()

  // Get symbol from query param
  const initialSymbol = searchParams.get('symbol')?.toUpperCase() || ''

  // Form state
  const [symbol, setSymbol] = useState(initialSymbol)
  const [symbolToLookup, setSymbolToLookup] = useState<string | null>(initialSymbol || null)
  const [pricePerShare, setPricePerShare] = useState<number>(0)
  const [riskPercentage, setRiskPercentage] = useState<number>(1) // 1% default
  const [stopLossPercentage, setStopLossPercentage] = useState<number>(10) // 10% default
  const [useCurrentPrice, setUseCurrentPrice] = useState(true)

  // Price lookup - only triggers on blur, not every keystroke
  const { data: priceData, isLoading: priceLoading } = usePrice(symbolToLookup)

  // Trigger lookup when symbol input loses focus
  const handleSymbolBlur = () => {
    if (symbol.length >= 1) {
      setSymbolToLookup(symbol.toUpperCase())
    } else {
      setSymbolToLookup(null)
    }
  }

  // Update price when lookup completes
  useEffect(() => {
    if (priceData && useCurrentPrice) {
      setPricePerShare(priceData.price)
    }
  }, [priceData, useCurrentPrice])

  // Calculate result
  const result: PositionSizingResult | null = useMemo(() => {
    if (!symbol || pricePerShare <= 0 || riskPercentage <= 0 || stopLossPercentage <= 0) return null
    return calculate({ symbol, pricePerShare, riskPercentage, stopLossPercentage })
  }, [symbol, pricePerShare, riskPercentage, stopLossPercentage, calculate])

  const portfolioValue = holdings?.reduce((sum, h) =>
    sum + (h.current_value ?? h.cost_basis), 0
  ) ?? 0

  // Risk presets
  const riskPresets = [
    { label: 'Conservative', value: 0.5 },
    { label: 'Moderate', value: 1 },
    { label: 'Aggressive', value: 2 },
    { label: 'Very Aggressive', value: 5 },
  ]

  // Stop loss presets
  const stopLossPresets = [
    { label: 'Tight (5%)', value: 5 },
    { label: 'Normal (10%)', value: 10 },
    { label: 'Wide (15%)', value: 15 },
    { label: 'Very Wide (20%)', value: 20 },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Position Sizing</h1>
        <p className="mt-1 text-sm text-gray-500">
          Calculate the optimal position size based on your risk tolerance
        </p>
      </div>

      {/* Portfolio context */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <span className="text-blue-700">Portfolio Value:</span>
          <span className="font-bold text-blue-900">{formatCurrency(portfolioValue)}</span>
        </div>
      </div>

      {/* Input form */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          {/* Symbol and Price */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Symbol"
                placeholder="AAPL"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onBlur={handleSymbolBlur}
              />
              {priceLoading && (
                <p className="mt-1 text-xs text-gray-400">Loading price...</p>
              )}
              {priceData && (
                <p className="mt-1 text-xs text-gray-500">
                  Current: {formatCurrency(priceData.price)}
                  <span className="text-gray-400 ml-1">
                    ({priceData.source === 'snapshot' ? 'from snapshot' : 'live'})
                  </span>
                </p>
              )}
            </div>

            <div>
              <Input
                label="Price per Share"
                type="number"
                min={0}
                step={0.01}
                value={pricePerShare || ''}
                onChange={(e) => {
                  setPricePerShare(parseFloat(e.target.value) || 0)
                  setUseCurrentPrice(false)
                }}
              />
              {priceData && !useCurrentPrice && (
                <button
                  type="button"
                  onClick={() => {
                    setPricePerShare(priceData.price)
                    setUseCurrentPrice(true)
                  }}
                  className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  Use current price
                </button>
              )}
            </div>
          </div>

          {/* Risk Parameters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk per Trade
              </label>
              <div className="flex gap-2 mb-2">
                {riskPresets.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setRiskPercentage(preset.value)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      riskPercentage === preset.value
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={0.1}
                  max={10}
                  step={0.1}
                  value={riskPercentage}
                  onChange={(e) => setRiskPercentage(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <span className="w-16 text-right font-medium">{riskPercentage.toFixed(1)}%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Risk {formatCurrency(portfolioValue * riskPercentage / 100)} on this trade
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stop Loss Distance
              </label>
              <div className="flex gap-2 mb-2">
                {stopLossPresets.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setStopLossPercentage(preset.value)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      stopLossPercentage === preset.value
                        ? 'bg-red-100 text-red-700 font-medium'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={1}
                  max={30}
                  step={1}
                  value={stopLossPercentage}
                  onChange={(e) => setStopLossPercentage(parseFloat(e.target.value))}
                  className="flex-1"
                />
                <span className="w-16 text-right font-medium">{stopLossPercentage.toFixed(0)}%</span>
              </div>
              {pricePerShare > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Stop at {formatCurrency(pricePerShare * (1 - stopLossPercentage / 100))}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Recommended Position</h2>
          </div>

          <div className="p-6">
            {/* Main result */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-green-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-green-900">
                  {result.maxShares}
                </div>
                <div className="text-sm text-green-700 mt-1">Shares</div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-blue-900">
                  {formatCurrency(result.positionValue)}
                </div>
                <div className="text-sm text-blue-700 mt-1">Position Value</div>
              </div>

              <div className="bg-purple-50 rounded-lg p-6 text-center">
                <div className="text-4xl font-bold text-purple-900">
                  {result.positionAllocation.toFixed(1)}%
                </div>
                <div className="text-sm text-purple-700 mt-1">Portfolio Weight</div>
              </div>
            </div>

            {/* Calculation breakdown */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Calculation Details</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500">Entry Price</div>
                  <div className="font-medium">{formatCurrency(result.pricePerShare)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500">Stop Loss Price</div>
                  <div className="font-medium text-red-600">{formatCurrency(result.stopLossPrice)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500">Loss per Share</div>
                  <div className="font-medium text-red-600">{formatCurrency(result.lossPerShare)}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-gray-500">Max Risk Amount</div>
                  <div className="font-medium text-red-600">{formatCurrency(result.riskAmount)}</div>
                </div>
              </div>
            </div>

            {/* Formula explanation */}
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">The Math</h4>
              <div className="font-mono text-xs text-gray-600 space-y-1">
                <div>Risk Amount = Portfolio Value × Risk % = {formatCurrency(result.portfolioValue)} × {result.riskPercentage}% = {formatCurrency(result.riskAmount)}</div>
                <div>Loss per Share = Entry - Stop = {formatCurrency(result.pricePerShare)} - {formatCurrency(result.stopLossPrice)} = {formatCurrency(result.lossPerShare)}</div>
                <div>Max Shares = Risk Amount ÷ Loss per Share = {formatCurrency(result.riskAmount)} ÷ {formatCurrency(result.lossPerShare)} = {result.maxShares}</div>
              </div>
            </div>

            {/* Warnings */}
            {result.positionAllocation > 20 && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This position would be {result.positionAllocation.toFixed(1)}% of your portfolio.
                  Consider reducing position size for better diversification.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <p className="text-gray-500">
            Enter a symbol and price to calculate the optimal position size
          </p>
        </div>
      )}
    </div>
  )
}
